import { useEffect, useState } from 'react'
import { UserMenu } from '@/components/commons/user-menu'

import { LogoutIcon } from '@/components/ui/icons'
import { logout } from '@/features/auth/store/actions.ts'
import { useAuthStore } from '@/features/auth/store/store'
import { GeoSelect } from '@/features/subgeo/components/geo-select'
import { LeafletMap } from '@/features/subgeo/components/leaflet-map'
import { MapLoading } from '@/features/subgeo/components/map-loading'
import { SidebarView } from '@/features/subgeo/components/subgeo-sidebar-view'
import { useGetGeos } from '@/features/subgeo/queries/use-get-geos'
import { useGetNeighborhoods } from '@/features/subgeo/queries/use-get-neighborhoods'
import { useGetSubgeos } from '@/features/subgeo/queries/use-get-subgeos'
import { useSubgeoStore } from '@/features/subgeo/store/subgeo-store'
import type { Neighborhood, Subgeo } from '@/features/subgeo/types'
import { SubGeoSearchParamsSchema } from '@/features/subgeo/types'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import { useTranslation } from '@/translations/i18n'

const SubGeoMapContent = ({
	isLoading,
	error,
	neighborhoods,
	selectedIds,
	shouldZoomToFit,
	subgeos,
	toggleNeighborhood,
	zoomToNeighborhoodId
}: {
	isLoading: boolean
	error: Error | null
	neighborhoods: Neighborhood[]
	selectedIds: Set<string>
	shouldZoomToFit: boolean
	subgeos: Subgeo[]
	toggleNeighborhood: (id: string) => void
	zoomToNeighborhoodId: string | null
}) => {
	if (isLoading) {
		return <MapLoading />
	}
	if (error) {
		return <div>Error loading neighborhoods: {error.message}</div>
	}
	return (
		<LeafletMap
			neighborhoods={neighborhoods}
			selectedIds={selectedIds}
			shouldZoomToFit={shouldZoomToFit}
			subgeos={subgeos}
			toggleNeighborhood={toggleNeighborhood}
			zoomToNeighborhoodId={zoomToNeighborhoodId}
		/>
	)
}

export const SubGeoTerritoryBuilder = () => {
	const user = useAuthStore((state) => state.user)
	const { t } = useTranslation()
	const { searchParams, setSearchParams } = useParsedSearchParams(
		SubGeoSearchParamsSchema
	)
	const geo = searchParams.geo || ''
	const {
		setSelectedGeo,
		neighborhoods,
		setNeighborhoods,
		selectedNeighborhoods,
		toggleNeighborhood,
		subgeos,
		setSubgeos,
		initializeSubgeoColors
	} = useSubgeoStore()

	const [shouldZoomToFit, setShouldZoomToFit] = useState(false)
	const [zoomToNeighborhoodId, setZoomToNeighborhoodId] = useState<
		string | null
	>(null)

	const { remappedData: geos, isLoading: isLoadingGeos } = useGetGeos(
		geo ? { geoCode: geo } : undefined
	)
	const selectedGeoObject = geos.find((g) => g.geoCode === geo)
	const canFetchData = !!geo && !isLoadingGeos && !!selectedGeoObject

	const {
		neighborhoods: fetchedNeighborhoods,
		isLoading,
		error
	} = useGetNeighborhoods(
		{
			geoCode: geo,
			responseType: 'extended'
		},
		{ enabled: canFetchData }
	)

	const { subgeos: fetchedSubgeos, isLoading: isLoadingSubgeos } =
		useGetSubgeos(
			{
				geoId: selectedGeoObject?.id || '',
				responseType: 'extended'
			},
			{ enabled: canFetchData }
		)

	const handleGeoChange = (value: string) => {
		setSelectedGeo(value)
		setSearchParams({ geo: value })
	}

	useEffect(() => {
		if (geo) {
			setSelectedGeo(geo)
		}
	}, [geo, setSelectedGeo])

	useEffect(() => {
		if (fetchedNeighborhoods.length > 0) {
			setNeighborhoods(fetchedNeighborhoods)
		}
	}, [fetchedNeighborhoods, setNeighborhoods])

	useEffect(() => {
		if (fetchedSubgeos.length > 0) {
			setSubgeos(fetchedSubgeos)
			initializeSubgeoColors(fetchedSubgeos)
		}
	}, [fetchedSubgeos, setSubgeos, initializeSubgeoColors])

	useEffect(() => {
		if (geo && neighborhoods.length > 0) {
			setShouldZoomToFit(true)
			const timer = setTimeout(() => setShouldZoomToFit(false), 1000)
			return () => clearTimeout(timer)
		}
	}, [geo, neighborhoods.length])

	const handleNeighborhoodClick = (neighborhoodId: string) => {
		setZoomToNeighborhoodId(neighborhoodId)

		setTimeout(() => {
			setZoomToNeighborhoodId(null)
		}, 1500)
	}

	return (
		<div className='relative m-0 h-dvh w-dvw overflow-hidden bg-neutral-white p-0'>
			{user && (
				<div className='fixed top-4 right-4 z-(--z-tooltip) flex rounded-[40px] border border-neutral-300 bg-neutral-white p-1 shadow-lg'>
					<UserMenu
						actions={[
							{
								label: t('common:logout'),
								icon: <LogoutIcon size={16} />,
								onClickCb: logout
							}
						]}
						size='sm'
						user={{
							name: user?.first_name ?? user.email,
							email: user.email,
							imageUrl: '',
							roleLabel: user.role
								? t(`auth:roles.${user.role}`)
								: undefined
						}}
					/>
				</div>
			)}

			<div className='absolute top-4 bottom-4 left-4 z-(--z-dropdown) flex w-[375px] flex-col gap-1.5'>
				<GeoSelect onChange={handleGeoChange} value={geo} />
				{geo && (
					<SidebarView
						isLoading={isLoadingSubgeos}
						onNeighborhoodClick={handleNeighborhoodClick}
					/>
				)}
			</div>

			<div className='absolute inset-0 z-(--z-map-base) overflow-hidden bg-neutral-100 [&_.leaflet-container]:h-full [&_.leaflet-container]:w-full [&_.leaflet-container]:bg-[#a0b8c5] [&_.leaflet-tile-pane]:absolute [&_.leaflet-tile-pane]:top-0 [&_.leaflet-tile-pane]:left-0'>
				<SubGeoMapContent
					error={error}
					isLoading={isLoading}
					neighborhoods={neighborhoods}
					selectedIds={selectedNeighborhoods}
					shouldZoomToFit={shouldZoomToFit}
					subgeos={subgeos}
					toggleNeighborhood={toggleNeighborhood}
					zoomToNeighborhoodId={zoomToNeighborhoodId}
				/>
			</div>
		</div>
	)
}
